import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ReviewDialog from "@/components/ReviewDialog";
import BookingActions from "@/components/BookingActions";
import { Button } from "@/components/ui/button";
import { money } from "@/lib/currency";

function bookingStatusPill(b) {
  if (b.status === "cancelled") return { cls: "bg-muted2/20 text-muted2", label: "Cancelled" };
  if (b.payment_status === "paid") return { cls: "bg-moss/10 text-moss", label: "Confirmed" };
  return { cls: "bg-terracotta/10 text-terracotta", label: "Payment pending" };
}

export default function TravelerDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [reviewing, setReviewing] = useState(null);

  const load = useCallback(() => api.get("/bookings/mine").then((r) => setBookings(r.data)), []);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    load();
  }, [user, loading, navigate, load]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#F4F2EC]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="overline">Traveler</div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <h1 className="font-serif text-4xl md:text-5xl mt-1 mb-6">Namaste, {user.name?.split(" ")[0]}</h1>
          <Link
            to="/preferences"
            data-testid="edit-preferences-btn"
            className="mt-3 inline-flex items-center gap-2 rounded-full bg-terracotta text-canvas px-5 py-2.5 text-sm hover:bg-terracotta/90 transition-colors"
          >
            <span className="text-base">✦</span> What are you seeking?
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white border border-subtle rounded-2xl p-6">
            <div className="overline">Journeys booked</div>
            <div className="font-serif text-4xl mt-2" data-testid="stat-my-bookings">{bookings.length}</div>
          </div>
          <div className="bg-white border border-subtle rounded-2xl p-6">
            <div className="overline">Confirmed</div>
            <div className="font-serif text-4xl mt-2">{bookings.filter(b => b.payment_status === "paid").length}</div>
          </div>
          <div className="bg-white border border-subtle rounded-2xl p-6 hidden md:block">
            <div className="overline">Countries visited</div>
            <div className="font-serif text-4xl mt-2">{new Set(bookings.map(b => b.country)).size}</div>
          </div>
        </div>

        <h2 className="font-serif text-3xl mb-5">Your journeys</h2>
        {bookings.length === 0 ? (
          <div className="bg-white border border-subtle rounded-2xl p-10 text-center">
            <p className="text-muted2 mb-4">You haven't booked anything yet.</p>
            <Link data-testid="empty-explore-btn" to="/discover" className="inline-block rounded-full bg-moss text-canvas px-6 py-3">Explore experiences</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {bookings.map((b) => (
              <Link key={b.id} to={`/experience/${b.experience_id}`} data-testid={`my-booking-${b.id}`} className="bg-white border border-subtle rounded-2xl overflow-hidden group">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={b.experience_image} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <div className="font-serif text-2xl leading-tight">{b.experience_title}</div>
                  <div className="text-sm text-muted2 mt-1">
                    {b.check_in}{b.check_out ? ` → ${b.check_out}` : ""} · {b.guests} guest{b.guests === 1 ? "" : "s"}
                    {b.nights ? ` · ${b.nights} night${b.nights === 1 ? "" : "s"}` : ""}
                  </div>
                  <div className="mt-3 flex justify-between items-center">
                    {(() => {
                      const pill = bookingStatusPill(b);
                      return (
                        <span className={`pillar-tag ${pill.cls}`} data-testid={`booking-status-${b.id}`}>
                          {pill.label}
                        </span>
                      );
                    })()}
                    <span className="font-medium">{money(b.total_price)}</span>
                  </div>
                  {b.payment_status === "paid" && !b.reviewed && b.status !== "cancelled" && (
                    <Button
                      data-testid={`review-btn-${b.id}`}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setReviewing(b); }}
                      className="w-full mt-4 rounded-full bg-white border border-moss text-moss hover:bg-moss hover:text-canvas transition-colors"
                    >
                      ★ Leave a review
                    </Button>
                  )}
                  {b.reviewed && (
                    <div className="mt-4 text-xs text-moss text-center">✓ Reviewed</div>
                  )}
                  <BookingActions booking={b} onChanged={load} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      {reviewing && (
        <ReviewDialog
          open={!!reviewing}
          onOpenChange={(o) => !o && setReviewing(null)}
          booking={reviewing}
          onSubmitted={load}
        />
      )}
      <Footer />
    </div>
  );
}
